import {
  autoPlacement,
  autoUpdate,
  offset,
  size,
  useFloating,
} from '@floating-ui/react';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from '@headlessui/react';
import UntitledIcon from '@payaca/untitled-icons';
import React, {
  ComponentProps,
  FC,
  Fragment,
  PropsWithChildren,
  ReactNode,
  useContext,
  useMemo,
} from 'react';

import { clstx } from '@payaca/utilities/cvaMerge';
import { FieldContext } from '../Field/Context';

export enum SelectSizeVariant {
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
}

export type ValueType = number | string | null;

export type SelectOption<
  TValueType extends ValueType,
  TMetaDataType = object,
> = {
  label: string;
  value: TValueType;
  disabled?: boolean;
  metadata?: TMetaDataType;
  groupId?: string;
};

export type OptionGroup = {
  id: string;
  label: string;
};

export type SelectProps<
  TValueType extends ValueType,
  TMultipleBoolean extends boolean,
  TMetaDataType = object,
> = {
  optionGroups?: OptionGroup[];
  options: SelectOption<TValueType, TMetaDataType>[];
  multiple?: TMultipleBoolean;
  onChange?: (
    value: TMultipleBoolean extends true ? TValueType[] : TValueType,
    actions: { close: () => void }
  ) => void;
  value?: TMultipleBoolean extends true ? TValueType[] : TValueType;
  disabled?: boolean;
  CustomOption?: FC<{
    option: SelectOption<TValueType, TMetaDataType>;
    active: boolean;
    selected: boolean;
    close: () => void;
  }>;
  CustomSelected?: FC<{
    selectedOptions: SelectOption<TValueType, TMetaDataType>[];
  }>;
  customButtonContent?: React.ReactNode;
  placeholder?: string;
  sizeVariant?: SelectSizeVariant;
  className?: string;
  listboxOptionClassName?: string;
  hideDropdownChevron?: boolean;
  /**
   * Whether the dropdown should animate its entrance and exit.
   * @default true
   *
   * Only really needed due to work around a HeadlessUI bug:
   * https://github.com/tailwindlabs/headlessui/issues/3562#issuecomment-2874994795
   */
  animate?: boolean;
  /**
   * Whether the selected value should wrap instead of truncating.
   * @default false
   */
  allowWrap?: boolean;
};

const Select = <
  TValueType extends ValueType,
  TMultipleBoolean extends boolean,
  TMetaDataType = object,
>({
  options,
  onChange,
  multiple,
  value,
  disabled,
  CustomOption,
  CustomSelected,
  placeholder,
  children,
  sizeVariant = SelectSizeVariant.MD,
  className,
  listboxOptionClassName,
  optionGroups,
  customButtonContent,
  hideDropdownChevron = false,
  animate = true,
  allowWrap = false,
}: PropsWithChildren<
  SelectProps<TValueType, TMultipleBoolean, TMetaDataType>
>) => {
  const { id, name, validationState } = useContext(FieldContext);
  const { refs, floatingStyles } = useFloating({
    transform: false,
    whileElementsMounted: autoUpdate,
    middleware: [
      autoPlacement({
        alignment: 'start',
        allowedPlacements: ['top-start', 'bottom-start'],
      }),
      size({
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight}px`,
            maxWidth: `${rects.reference.width}px`,
          });
        },
        padding: 8,
      }),
      offset({
        mainAxis: 4,
      }),
    ],
  });

  const selectedOptions = options.filter((o) =>
    multiple ? (value as TValueType[])?.includes(o.value) : o.value === value
  );

  const borderColour = useMemo(() => {
    switch (validationState?.isValid) {
      case true:
        return 'border-green-500';
      case false:
        return 'border-red-500';
      default:
        return 'border-gray-200';
    }
  }, [validationState?.isValid]);

  const sizeClasses = useMemo(() => {
    switch (sizeVariant) {
      case SelectSizeVariant.SM:
        return 'py-2 px-3';
      case SelectSizeVariant.MD:
        return 'py-3 px-4';
      case SelectSizeVariant.LG:
        return 'py-3 px-4 sm:p-5';
    }
  }, [sizeVariant]);

  const close = () => {
    const escDown = new KeyboardEvent('keydown', {
      key: 'Escape',
      code: 'Escape',
      bubbles: true,
      cancelable: true,
    });

    refs.floating.current?.dispatchEvent(escDown);
  };

  const actions = { close };

  return (
    <Listbox
      multiple={multiple}
      disabled={disabled}
      onChange={(value) => onChange?.(value, actions)}
      value={value}
      name={name}
    >
      {({ open }) => (
        <>
          <ListboxButton
            ref={refs.setReference}
            className={clstx(
              `font-inherit relative text-left shadow-sm disabled:pointer-events-none disabled:text-inherit disabled:opacity-50 block w-full border pr-9 sm:pr-9 rounded-md bg-white text-base focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400`,
              borderColour,
              sizeClasses,
              className
            )}
          >
            {customButtonContent ? (
              customButtonContent
            ) : (
              <SelectButtonContent
                multiple={multiple}
                disabled={disabled}
                value={value}
                selectedOptions={selectedOptions}
                CustomSelected={CustomSelected}
                placeholder={placeholder}
                onChange={(value) => onChange?.(value, actions)}
                allowWrap={allowWrap}
              />
            )}
            {!hideDropdownChevron && (
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <UntitledIcon
                  name="chevron-down"
                  className="h-5 w-5 text-gray-600"
                />
              </span>
            )}
          </ListboxButton>

          <TransitionWrapper open={open} animate={animate}>
            <ListboxOptions
              id={id}
              ref={refs.setFloating}
              className="z-dropdown m-0 block flex w-full list-none flex-col gap-1 overflow-y-auto rounded-md border border-gray-200 bg-white p-1 text-base focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400"
              style={floatingStyles}
              modal={false}
              portal
            >
              <Options
                options={options}
                optionClassName={listboxOptionClassName}
                CustomOption={CustomOption}
                optionGroups={optionGroups}
                OptionComponent={ListboxOption as any}
                close={close}
              />
              {children}
            </ListboxOptions>
          </TransitionWrapper>
        </>
      )}
    </Listbox>
  );
};

export default Select;

export const Options = <
  TValueType extends ValueType,
  TMultipleBoolean extends boolean,
  TMetadataType = object,
>({
  options,
  optionClassName,
  CustomOption,
  optionGroups,
  OptionComponent,
  onOptionClick,
  close,
}: Pick<
  SelectProps<TValueType, TMultipleBoolean, TMetadataType>,
  'options' | 'CustomOption' | 'optionGroups'
> & {
  optionClassName?: string;
  OptionComponent: FC<
    Pick<
      ComponentProps<typeof ListboxOption>,
      'value' | 'disabled' | 'className' | 'children' | 'onClick'
    >
  >;
  // @deprecated - do not use this - leads to antipatterns
  onOptionClick?: (
    event: React.MouseEvent<HTMLDivElement>,
    option: SelectOption<TValueType, TMetadataType>
  ) => void;
  close: () => void;
}) => {
  const groupIds = optionGroups?.map((g) => g.id) || [];

  return (
    <>
      {optionGroups?.map((group) => {
        return (
          <>
            <OptionComponent
              key={group.id}
              value={group.id}
              disabled={true}
              className={`px-4 py-2 text-sm font-medium uppercase text-gray-400`}
            >
              {group.label}
            </OptionComponent>
            {options
              .filter((o) => o.groupId === group.id)
              .map((option) => (
                <OptionComponent
                  // @ts-expect-error - in HeadlessUI v2, onMouseDown is used instead of onClick - although isn't typed correctly
                  onMouseDown={(e: React.MouseEvent<HTMLDivElement>) =>
                    onOptionClick?.(e, option)
                  }
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  className={({
                    focus,
                    selected,
                  }: {
                    focus: boolean;
                    selected: boolean;
                  }) =>
                    `flex cursor-pointer flex-row items-center gap-2 rounded-sm px-4 py-2 ${
                      selected ? 'bg-gray-300' : focus ? 'bg-gray-200' : ''
                    }
                     ${optionClassName}`
                  }
                >
                  {({ focus, selected }) => (
                    <>
                      {CustomOption ? (
                        <CustomOption
                          option={option}
                          active={focus}
                          selected={selected}
                          close={close}
                        />
                      ) : (
                        <span>{option.label}</span>
                      )}
                      {selected && (
                        <UntitledIcon
                          name="check"
                          className="ml-auto h-3.5 w-3.5 shrink-0 text-blue-600"
                        />
                      )}
                    </>
                  )}
                </OptionComponent>
              ))}
          </>
        );
      })}
      {options
        .filter((o) => (o.groupId ? !groupIds.includes(o.groupId) : true))
        .map((option) => (
          <OptionComponent
            // @ts-expect-error - in HeadlessUI v2, onMouseDown is used instead of onClick - although isn't typed correctly
            onMouseDown={(e: React.MouseEvent<HTMLDivElement>) =>
              onOptionClick?.(e, option)
            }
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            className={({
              focus,
              selected,
            }: {
              focus: boolean;
              selected: boolean;
            }) =>
              `data-[disabled]:text-gray-400 data-[disabled]:cursor-not-allowed flex cursor-pointer flex-row items-center gap-2 rounded-sm px-4 py-2 ${
                selected ? 'bg-gray-300' : focus ? 'bg-gray-200' : ''
              } ${optionClassName}`
            }
          >
            {({ focus, selected }) => (
              <>
                {CustomOption ? (
                  <CustomOption
                    option={option}
                    active={focus}
                    selected={selected}
                    close={close}
                  />
                ) : (
                  <span>{option.label}</span>
                )}
                {selected && (
                  <UntitledIcon
                    name="check"
                    className="ml-auto h-3.5 w-3.5 shrink-0 text-blue-600"
                  />
                )}
              </>
            )}
          </OptionComponent>
        ))}
    </>
  );
};

export const DismissableSelectTag: FC<
  PropsWithChildren<{ onDismiss?: () => void }>
> = ({ children, onDismiss }) => {
  return (
    <div className="relative z-10 flex flex-nowrap items-center rounded-full border border-gray-200 p-1 ps-2">
      <span className="mr-1 max-w-[150px] truncate whitespace-nowrap">
        {children}
      </span>
      {!!onDismiss && (
        <button
          type="button"
          onPointerDown={(e) => {
            e.preventDefault();
            onDismiss?.();
          }}
          className="me-1 ms-1 inline-flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded-full bg-gray-200 text-base text-gray-800 transition-all hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 dark:bg-gray-700/50 dark:text-gray-400 dark:hover:bg-gray-700"
        >
          <UntitledIcon name="x-close" />
        </button>
      )}
    </div>
  );
};

export const SelectButtonContent = <
  TValueType extends ValueType,
  TMultipleBoolean extends boolean,
  TMetadataType = object,
>({
  multiple,
  disabled,
  placeholder,
  CustomSelected,
  onChange,
  selectedOptions,
  value,
  allowWrap = false,
}: Pick<
  SelectProps<TValueType, TMultipleBoolean, TMetadataType>,
  | 'multiple'
  | 'disabled'
  | 'placeholder'
  | 'CustomSelected'
  | 'onChange'
  | 'value'
  | 'allowWrap'
> & {
  selectedOptions: SelectOption<TValueType, TMetadataType>[];
}) => {
  const renderDismissableTags =
    multiple && !disabled && selectedOptions.length && !CustomSelected;

  return (
    <div
      className={`block ${allowWrap ? '' : 'truncate'} ${
        renderDismissableTags ? '-my-[calc(0.333rem+1px)]' : ''
      }`}
    >
      {selectedOptions.length === 0 && (
        <span className="text-gray-600">{placeholder || 'Please select'}</span>
      )}
      {CustomSelected ? (
        <CustomSelected selectedOptions={selectedOptions} />
      ) : multiple && !disabled ? (
        <div className="flex flex-wrap gap-1">
          {selectedOptions.map((x) => {
            return (
              <DismissableSelectTag
                key={x.value}
                onDismiss={
                  x.disabled
                    ? undefined
                    : () => {
                        (onChange as (value: TValueType[]) => void)?.(
                          Array.isArray(value)
                            ? value.filter((v) => v !== x.value)
                            : []
                        );
                      }
                }
              >
                {x.label}
              </DismissableSelectTag>
            );
          })}
        </div>
      ) : (
        selectedOptions.map((o) => o.label).join(', ')
      )}
    </div>
  );
};

const TransitionWrapper = ({
  children,
  animate,
  open,
}: {
  children: ReactNode;
  animate: boolean;
  open: boolean;
}) => {
  if (!animate) {
    return <>{children}</>;
  }
  return (
    <Transition
      show={open}
      appear
      as={Fragment}
      enter="transition duration-100 ease-out"
      enterFrom="scale-95 opacity-0"
      enterTo="scale-100 opacity-100"
      leave="transition duration-75 ease-out"
      leaveFrom="scale-100 opacity-100"
      leaveTo="scale-95 opacity-0"
    >
      {children}
    </Transition>
  );
};
